<template>
  <v-dialog
    v-model="dialogOpen"
    :width="isFullscreen ? '100vw' : customWidth + 'px'"
    :max-width="isFullscreen ? '100vw' : 'none'"
    :fullscreen="isFullscreen"
    :persistent="isResizing"
    transition="dialog-fade-transition"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <template v-slot:activator="{ on, attrs }">
      <slot
        name="activatorButton"
        :on="on"
        :attrs="attrs"
      />
    </template>

    <!-- Premium Dialog -->
    <div
      ref="dialogCard"
      class="premium-dialog"
      :class="{
        'is-fullscreen': isFullscreen,
        'is-resizing': isResizing
      }"
      :style="dialogStyle"
    >
      <!-- Header Section -->
      <div class="dialog-header-section">
        <div class="header-background" />

        <div class="header-content-wrapper">
          <v-row
            no-gutters
            class="align-center header-row"
          >
            <!-- Icon with Backdrop -->
            <div
              v-if="$slots.icon"
              class="icon-container"
            >
              <div class="icon-backdrop" />
              <div class="icon-content">
                <slot name="icon" />
              </div>
            </div>

            <!-- Title Section -->
            <div
              class="title-section"
              :class="{ 'with-icon': $slots.icon }"
            >
              <h1 class="dialog-title-text">
                {{ title }}
              </h1>
              <p
                v-if="subtitle"
                class="dialog-subtitle-text"
              >
                {{ subtitle }}
              </p>
            </div>

            <!-- Window Controls -->
            <v-spacer />
            <div class="window-controls">
              <!-- Expand/Shrink Button -->
              <v-btn
                icon
                small
                ripple
                class="window-btn expand-btn"
                :title="isFullscreen ? 'Restore' : 'Maximize'"
                @click="toggleFullscreen"
              >
                <v-icon size="18">
                  {{ isFullscreen ? 'mdi-window-restore' : 'mdi-window-maximize' }}
                </v-icon>
              </v-btn>

              <!-- Close Button -->
              <v-btn
                icon
                small
                ripple
                class="window-btn close-button"
                title="Close"
                @click="handleClose"
              >
                <v-icon size="20">
                  mdi-close
                </v-icon>
              </v-btn>
            </div>
          </v-row>
        </div>
      </div>

      <!-- Content Section -->
      <div
        class="dialog-body-section"
        :style="bodyStyle"
      >
        <v-row
          no-gutters
          style="width: 100%; height: 100%;"
        >
          <slot name="content" />
        </v-row>
      </div>

      <!-- Footer with Buttons -->
      <div class="dialog-footer-section">
        <div class="footer-actions">
          <v-row
            v-if="$slots.primaryButton"
            no-gutters
            class="button-wrapper primary-button-wrapper"
          >
            <slot name="primaryButton" />
          </v-row>
          <v-row
            no-gutters
            class="button-wrapper secondary-button-wrapper"
          >
            <slot name="cancelButton" />
          </v-row>
        </div>
      </div>

      <!-- Resize Handles (only when not fullscreen) -->
      <template v-if="!isFullscreen">
        <!-- Bottom-right corner resize -->
        <div
          class="resize-handle resize-handle-se"
          @mousedown="startResize($event, 'se')"
          @touchstart="startResize($event, 'se')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              d="M 2 14 L 14 14 L 14 2"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              opacity="0.4"
            />
            <path
              d="M 6 14 L 14 14 L 14 6"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              opacity="0.6"
            />
            <path
              d="M 10 14 L 14 14 L 14 10"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              opacity="0.8"
            />
          </svg>
        </div>

        <!-- Right edge resize -->
        <div
          class="resize-handle resize-handle-e"
          @mousedown="startResize($event, 'e')"
          @touchstart="startResize($event, 'e')"
        />

        <!-- Bottom edge resize -->
        <div
          class="resize-handle resize-handle-s"
          @mousedown="startResize($event, 's')"
          @touchstart="startResize($event, 's')"
        />
      </template>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "StwPower",
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: null
    },
    dialogModel: {
      type: Boolean,
      required: true
    },
    defaultWidth: {
      type: Number,
      default: 520
    },
    defaultHeight: {
      type: Number,
      default: null
    },
    minWidth: {
      type: Number,
      default: 320
    },
    minHeight: {
      type: Number,
      default: 300
    },
    rememberSize: {
      type: Boolean,
      default: true
    },
    storageKey: {
      type: String,
      default: "baseDialogSize"
    }
  },

  data() {
    return {
      dialogOpen: this.dialogModel,
      isFullscreen: false,
      isResizing: false,
      resizeDirection: null,
      resizeStart: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      },
      customWidth: this.defaultWidth,
      customHeight: this.defaultHeight
    };
  },

  computed: {
    dialogStyle() {
      if (this.isFullscreen) {
        return {
          width: "100vw",
          height: "100vh",
          borderRadius: "0",
          maxHeight: "100vh"
        };
      }

      const style = {
        width: this.customWidth + "px",
        maxWidth: "95vw"
      };

      if (this.customHeight) {
        style.height = this.customHeight + "px";
        style.maxHeight = "95vh";
      }

      return style;
    },

    bodyStyle() {
      if (this.isFullscreen) {
        return {
          maxHeight: "calc(100vh - 200px)",
          height: "calc(100vh - 200px)"
        };
      }

      if (this.customHeight) {
        return {
          height: (this.customHeight - 180) + "px",
          maxHeight: (this.customHeight - 180) + "px"
        };
      }

      return {
        maxHeight: "60vh"
      };
    }
  },

  watch: {
    dialogModel(val) {
      this.dialogOpen = val;
      if (val) {
        this.loadSize();
      }
    },

    dialogOpen(val) {
      if (val !== this.dialogModel) {
        this.$emit("update:dialogModel", val);
      }
      if (!val) {
        this.isFullscreen = false;
      }
    }
  },

  mounted() {
    if (this.dialogModel) {
      this.loadSize();
    }
  },

  beforeDestroy() {
    this.stopResize();
  },

  methods: {
    handleClose() {
      if (this.isResizing) return;
      this.dialogOpen = false;
      this.$emit("close");
      this.$emit("update:dialogModel", false);
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },

    startResize(event, direction) {
      event.preventDefault();
      event.stopPropagation();

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      this.isResizing = true;
      this.resizeDirection = direction;
      this.resizeStart = {
        x: clientX,
        y: clientY,
        width: this.customWidth,
        height: this.customHeight || (this.$refs.dialogCard ? this.$refs.dialogCard.offsetHeight : 500)
      };

      document.addEventListener("mousemove", this.doResize);
      document.addEventListener("mouseup", this.stopResize);
      document.addEventListener("touchmove", this.doResize, { passive: false });
      document.addEventListener("touchend", this.stopResize);

      document.body.style.cursor = this.getCursor(direction);
      document.body.style.userSelect = "none";
    },

    doResize(event) {
      if (!this.isResizing) return;
      event.preventDefault();

      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      const deltaX = clientX - this.resizeStart.x;
      const deltaY = clientY - this.resizeStart.y;

      if (this.resizeDirection.includes("e")) {
        const newWidth = this.resizeStart.width + (deltaX * 2);
        this.customWidth = Math.max(this.minWidth, Math.min(newWidth, window.innerWidth * 0.95));
      }

      if (this.resizeDirection.includes("s")) {
        const newHeight = this.resizeStart.height + deltaY;
        this.customHeight = Math.max(this.minHeight, Math.min(newHeight, window.innerHeight * 0.95));
      }
    },

    stopResize() {
      if (!this.isResizing) return;

      this.isResizing = false;
      this.resizeDirection = null;

      document.removeEventListener("mousemove", this.doResize);
      document.removeEventListener("mouseup", this.stopResize);
      document.removeEventListener("touchmove", this.doResize);
      document.removeEventListener("touchend", this.stopResize);

      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      this.saveSize();
      this.triggerChartReflow();
    },

    getCursor(direction) {
      const cursors = {
        "e": "ew-resize",
        "s": "ns-resize",
        "se": "nwse-resize"
      };
      return cursors[direction] || "default";
    },

    saveSize() {
      if (!this.rememberSize) return;
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          width: this.customWidth,
          height: this.customHeight
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
    },

    loadSize() {
      if (!this.rememberSize) return;
      try {
        const saved = localStorage.getItem(this.storageKey);
        if (saved) {
          const size = JSON.parse(saved);
          if (size.width) this.customWidth = Math.max(this.minWidth, size.width);
          if (size.height) this.customHeight = Math.max(this.minHeight, size.height);
        }
      } catch (e) {
        // Ignore parse errors
      }
    },

    triggerChartReflow() {
      // Trigger Highcharts reflow if present
      this.$nextTick(() => {
        if (window.Highcharts && window.Highcharts.charts) {
          window.Highcharts.charts.forEach((chart) => {
            if (chart) {
              chart.setSize(null, null, false);
              chart.reflow();
            }
          });
        }
        // Dispatch resize event for other listeners
        window.dispatchEvent(new Event("resize"));
      });
    },

    resetSize() {
      this.customWidth = this.defaultWidth;
      this.customHeight = this.defaultHeight;
      this.isFullscreen = false;
      this.saveSize();
      this.triggerChartReflow();
    }
  }
};
</script>

<style scoped>
/* ==================== PREMIUM DIALOG CONTAINER ==================== */
.premium-dialog {
  background-color: var(--color-surface);
  border-radius: 16px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.15),
    0 0 1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  animation: slideUpIn 0.3s ease-out;
  transition: width 0.1s ease, height 0.1s ease;
}

.premium-dialog.is-resizing {
  transition: none;
  user-select: none;
}

.premium-dialog.is-fullscreen {
  border-radius: 0;
  animation: fadeIn 0.2s ease-out;
}

@keyframes slideUpIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ==================== HEADER SECTION ==================== */
.dialog-header-section {
  position: relative;
  padding: 0;
  overflow: hidden;
  flex-shrink: 0;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary) 50%,
    var(--color-primary-light) 100%
  );
  opacity: 0.05;
  z-index: 0;
}

.header-content-wrapper {
  position: relative;
  z-index: 1;
  padding: 20px 20px 16px;
}

.header-row {
  width: 100%;
}

/* Icon */
.icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.icon-backdrop {
  position: absolute;
  width: 42px;
  height: 42px;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-light) 100%
  );
  border-radius: 10px;
  opacity: 0.15;
}

.icon-content {
  position: relative;
  z-index: 2;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
}

.icon-content >>> .v-icon {
  color: var(--color-primary) !important;
}

/* Title */
.title-section {
  flex: 1;
  min-width: 0;
  padding-right: 12px;
}

.dialog-title-text {
  margin: 0;
  padding: 0;
  font-size: 1.35em;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.dialog-subtitle-text {
  margin: 4px 0 0 0;
  padding: 0;
  font-size: 0.85em;
  font-weight: 400;
  color: var(--color-text-muted);
  line-height: 1.4;
}

/* Window Controls */
.window-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.window-btn {
  color: var(--color-text-muted) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  width: 32px !important;
  height: 32px !important;
}

.window-btn:hover {
  color: var(--color-text) !important;
  background-color: var(--color-overlay) !important;
}

.expand-btn:hover {
  color: var(--color-primary) !important;
}

.close-button:hover {
  color: #e53935 !important;
  background-color: rgba(229, 57, 53, 0.1) !important;
}

.close-button:active {
  transform: scale(0.92);
}

/* ==================== BODY SECTION ==================== */
.dialog-body-section {
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: var(--color-surface);
  position: relative;
  flex: 1;
  min-height: 0;
}

.dialog-body-section::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.dialog-body-section::-webkit-scrollbar-track {
  background: transparent;
}

.dialog-body-section::-webkit-scrollbar-thumb {
  background: linear-gradient(
    to bottom,
    var(--color-primary),
    var(--color-primary-light)
  );
  border-radius: 4px;
  opacity: 0.3;
}

.dialog-body-section::-webkit-scrollbar-thumb:hover {
  opacity: 0.5;
}

/* ==================== FOOTER SECTION ==================== */
.dialog-footer-section {
  padding: 16px 20px 20px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    var(--color-bg) 100%
  );
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.footer-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.button-wrapper {
  width: 100%;
}

/* Primary Button */
.primary-button-wrapper >>> .v-btn {
  width: 100% !important;
  height: 42px !important;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-light) 100%
  ) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 0.95em !important;
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 12px rgba(74, 126, 186, 0.2) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.primary-button-wrapper >>> .v-btn:hover {
  box-shadow: 0 6px 16px rgba(74, 126, 186, 0.3) !important;
  transform: translateY(-1px) !important;
}

.primary-button-wrapper >>> .v-btn:active {
  transform: translateY(0) !important;
  box-shadow: 0 2px 8px rgba(74, 126, 186, 0.2) !important;
}

/* Secondary Button */
.secondary-button-wrapper >>> .v-btn {
  width: 100% !important;
  height: 42px !important;
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
  font-weight: 500 !important;
  font-size: 0.95em !important;
  border-radius: 8px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border: 1.5px solid var(--color-border) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.secondary-button-wrapper >>> .v-btn:hover {
  background-color: var(--color-overlay) !important;
  border-color: var(--color-primary) !important;
  transform: translateY(-1px) !important;
}

.secondary-button-wrapper >>> .v-btn:active {
  transform: translateY(0) !important;
}

/* Disabled */
.primary-button-wrapper >>> .v-btn:disabled,
.secondary-button-wrapper >>> .v-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* ==================== RESIZE HANDLES ==================== */
.resize-handle {
  position: absolute;
  z-index: 10;
  user-select: none;
  touch-action: none;
}

/* Bottom-right corner (main handle) */
.resize-handle-se {
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  cursor: nwse-resize;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  color: var(--color-text-muted);
  transition: color 0.2s ease;
  padding: 2px;
}

.resize-handle-se:hover {
  color: var(--color-primary);
}

.resize-handle-se svg {
  display: block;
}

/* Right edge */
.resize-handle-e {
  top: 60px;
  right: -3px;
  width: 6px;
  bottom: 24px;
  cursor: ew-resize;
  transition: background-color 0.2s ease;
}

.resize-handle-e:hover {
  background-color: var(--color-primary);
  opacity: 0.2;
}

/* Bottom edge */
.resize-handle-s {
  bottom: -3px;
  left: 20px;
  right: 24px;
  height: 6px;
  cursor: ns-resize;
  transition: background-color 0.2s ease;
}

.resize-handle-s:hover {
  background-color: var(--color-primary);
  opacity: 0.2;
}

/* ==================== FULLSCREEN MODE ==================== */
.premium-dialog.is-fullscreen {
  width: 100vw !important;
  height: 100vh !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
}

.premium-dialog.is-fullscreen .dialog-body-section {
  max-height: none !important;
  height: auto !important;
}

/* ==================== RESPONSIVE DESIGN ==================== */
@media (max-width: 600px) {
  .premium-dialog {
    border-radius: 12px;
  }

  .header-content-wrapper {
    padding: 18px 16px 12px;
  }

  .dialog-title-text {
    font-size: 1.2em;
  }

  .dialog-subtitle-text {
    font-size: 0.8em;
  }

  .dialog-body-section {
    padding: 16px;
  }

  .dialog-footer-section {
    padding: 12px 16px 16px;
  }

  .icon-backdrop,
  .icon-content {
    width: 38px;
    height: 38px;
  }

  .window-btn {
    width: 30px !important;
    height: 30px !important;
  }

  .primary-button-wrapper >>> .v-btn,
  .secondary-button-wrapper >>> .v-btn {
    height: 40px !important;
    font-size: 0.9em !important;
  }

  /* Hide some resize handles on mobile */
  .resize-handle-e,
  .resize-handle-s {
    display: none;
  }
}
</style>
